import { useState, type FormEvent } from 'react';
import type { ClientBody } from '../../api/types';
import { Button, ChipsInput, ErrorBanner, Field, Input, Textarea } from '../../components';
import { normalizeDomain } from '../../lib/text';

export interface ClientFormValues {
  name: string;
  industry: string;
  domains: string[];
  notes: string;
}

export function toClientBody(v: ClientFormValues): ClientBody {
  return {
    name: v.name.trim(),
    industry: v.industry.trim() || null,
    domains: v.domains,
    notes: v.notes.trim() || null,
  };
}

/** Shared by "New client" and the client detail edit card. */
export function ClientForm({
  initial,
  submitLabel,
  busy,
  error,
  onSubmit,
  onCancel,
  requireDirty = false,
}: {
  initial: ClientFormValues;
  submitLabel: string;
  busy: boolean;
  error: unknown;
  onSubmit: (values: ClientFormValues) => void;
  onCancel?: () => void;
  /** Disable submit until something changed (edit forms). */
  requireDirty?: boolean;
}) {
  const [v, setV] = useState(initial);
  const [nameError, setNameError] = useState<string | null>(null);
  const dirty = JSON.stringify(v) !== JSON.stringify(initial);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!v.name.trim()) {
      setNameError('Name is required.');
      return;
    }
    setNameError(null);
    onSubmit(v);
  };

  return (
    <form className="space-y-3" onSubmit={submit}>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Name" error={nameError}>
          <Input value={v.name} onChange={(e) => setV({ ...v, name: e.target.value })} maxLength={200} />
        </Field>
        <Field label="Industry">
          <Input value={v.industry} onChange={(e) => setV({ ...v, industry: e.target.value })} maxLength={120} />
        </Field>
      </div>
      <Field label="Email domains" hint="Used to match calendar attendees to this client. Press Enter after each domain.">
        <ChipsInput
          value={v.domains}
          onChange={(domains) => setV({ ...v, domains })}
          normalize={normalizeDomain}
          placeholder="acme.com"
        />
      </Field>
      <Field label="Notes">
        <Textarea rows={4} value={v.notes} onChange={(e) => setV({ ...v, notes: e.target.value })} maxLength={10000} />
      </Field>
      <ErrorBanner error={error} />
      <div className="flex justify-end gap-2">
        {onCancel && <Button onClick={onCancel}>Cancel</Button>}
        <Button type="submit" variant="primary" loading={busy} disabled={requireDirty && !dirty}>
          {submitLabel}
        </Button>
      </div>
    </form>
  );
}
